import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import {
    EmailInvitationRecord,
    InvitationContactRecord,
} from './invitation';
import { useSaveContactRequest, useSaveEmailInvitation } from './invitation.queries';
import { InvitationCustomAttributes, readContactAttributes } from './InvitationCustomAttributes';

interface EditInvitationDialogProps {
    open: boolean;
    mode: 'email' | 'contact';
    email?: EmailInvitationRecord;
    contact?: InvitationContactRecord;
    onClose: () => void;
}

const ReadOnlyField = ({ id, label, value }: { id: string; label: string; value?: string }) => (
    <div className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base" id={`${id}-label`}>{label}</span>
        <p id={id} className="tw-whitespace-pre-wrap tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{value}</p>
    </div>
);

const EditInvitationDialog = ({ open, mode, email, contact, onClose }: EditInvitationDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const attributes = useMemo(() => readContactAttributes(dataModel), [dataModel]);
    const form = useForm();
    const { reset, getValues } = form;
    const saveEmail = useSaveEmailInvitation();
    const saveContact = useSaveContactRequest();
    const saving = saveEmail.isPending || saveContact.isPending;
    const customValues = mode === 'email' ? email?.customValues : contact?.customValues;

    useEffect(() => {
        if (!open) {
            return;
        }
        reset(customValues ?? {});
    }, [open, customValues, reset]);

    const collectedValues = () => {
        const next: Record<string, unknown> = { ...(customValues ?? {}) };
        const values = getValues();
        attributes.forEach((attribute) => {
            if (values[attribute.name] !== undefined) {
                next[attribute.name] = values[attribute.name];
            }
        });
        return next;
    };

    const save = () => {
        if (saving) {
            return;
        }
        const onSuccess = () => {
            toast.success('INVITATION_SUCCESSFULLY_SENT');
            onClose();
        };
        const onError = () => {
            toast.error('SAVE_ERROR_MESSAGE');
        };
        if (mode === 'email' && email) {
            saveEmail.mutate({ ...email, customValues: collectedValues() }, { onSuccess, onError });
            return;
        }
        if (mode === 'contact' && contact) {
            saveContact.mutate({
                id: contact.id,
                customValues: collectedValues(),
                organizationId: contact.organizationId,
            }, { onSuccess, onError });
        }
    };

    const emailAddress = mode === 'email' ? email?.invitedEmailAddress : contact?.email;
    const invitationType = mode === 'email' ? email?.invitationType : contact?.invitationType;
    const reason = mode === 'email' ? email?.text : contact?.text;

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent variant="large">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>
                        {t('INVITATION.EDIT_INVITATION')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-max-h-[70vh] tw-overflow-y-auto">
                    <FormProvider {...form}>
                        <div className="tw-flex tw-flex-col tw-gap-4">
                            <ReadOnlyField id="invitation-email-address" label={t('EMAIL')} value={emailAddress} />
                            <ReadOnlyField
                                id="invitation-invitee-type"
                                label={t('INVITATION.INVITATION_TYPE')}
                                value={invitationType ? t(invitationType) : ''}
                            />
                            <ReadOnlyField id="invitation-reason-input" label={t('REASON_FOR_INVITE')} value={reason} />
                            <InvitationCustomAttributes />
                        </div>
                    </FormProvider>
                </DialogBody>
                <DialogFooter>
                    <Button id="submit-invitation" type="button" loading={saving} onClick={save}>
                        {t('SAVE')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { EditInvitationDialog };
